import {
  AdditiveBlending,
  BufferAttribute,
  BufferGeometry,
  Color,
  NormalBlending,
  Points,
  ShaderMaterial,
  UniformsLib,
  UniformsUtils,
  Vector3,
  type Scene,
  type Texture,
} from 'three';

/**
 * Un nuage de particules a tout faire : poussiere des chenilles, traceurs des
 * obus, etincelles d'impact, fumee.
 *
 * Les quatre effets ont les memes besoins -- une position, une vitesse, une
 * duree de vie, une couleur et une taille par particule -- et aucun ne les
 * obtient de `PointsMaterial`, qui n'offre qu'une taille et une opacite
 * globales. Un petit ShaderMaterial coute vingt lignes de GLSL et evite
 * d'ecrire quatre fois la meme boucle.
 *
 * L'allocation est un anneau : quand le tampon est plein, la plus vieille
 * particule est ecrasee. C'est volontaire -- jamais d'allocation en vol,
 * jamais de pic de GC au milieu d'une rafale.
 * cf. doc/06-boucle-de-jeu-et-perf.md
 */

export type BlendMode = 'additive' | 'soft';

export interface FieldOptions {
  capacity: number;
  texture: Texture;
  /**
   * `additive` pour tout ce qui emet de la lumiere (etincelles, traceurs,
   * poussiere au soleil), `soft` pour ce qui en absorbe (fumee).
   */
  blend: BlendMode;
  /** Acceleration verticale, en m/s^2. Negative pour retomber. */
  gravity?: number;
  /** Frottement visqueux : fraction de vitesse perdue par seconde. */
  drag?: number;
  /** Exposant de la rampe d'opacite. > 1 : disparait tard, puis vite. */
  fadePower?: number;
}

export class ParticleField {
  readonly points: Points;

  private readonly positions: Float32Array;
  private readonly colors: Float32Array;
  private readonly sizes: Float32Array;
  private readonly alphas: Float32Array;

  private readonly velocities: Float32Array;
  private readonly age: Float32Array;
  private readonly life: Float32Array;
  private readonly baseColor: Float32Array;
  private readonly baseSize: Float32Array;

  private cursor = 0;
  private readonly capacity: number;
  private readonly gravity: number;
  private readonly drag: number;
  private readonly fadePower: number;

  private readonly geometry = new BufferGeometry();
  private readonly material: ShaderMaterial;
  private readonly onResize = () => this.refreshScale();

  constructor(scene: Scene, opts: FieldOptions) {
    this.capacity = opts.capacity;
    this.gravity = opts.gravity ?? 0;
    this.drag = opts.drag ?? 0;
    this.fadePower = opts.fadePower ?? 1;

    const n = this.capacity;
    this.positions = new Float32Array(n * 3);
    this.colors = new Float32Array(n * 3);
    this.sizes = new Float32Array(n);
    this.alphas = new Float32Array(n);
    this.velocities = new Float32Array(n * 3);
    this.age = new Float32Array(n);
    this.life = new Float32Array(n);
    this.baseColor = new Float32Array(n * 3);
    this.baseSize = new Float32Array(n);
    // Toutes les particules naissent mortes.
    this.age.fill(1);
    this.life.fill(1);

    this.geometry.setAttribute('position', new BufferAttribute(this.positions, 3));
    this.geometry.setAttribute('aColor', new BufferAttribute(this.colors, 3));
    this.geometry.setAttribute('aSize', new BufferAttribute(this.sizes, 1));
    this.geometry.setAttribute('aAlpha', new BufferAttribute(this.alphas, 1));

    this.material = new ShaderMaterial({
      uniforms: UniformsUtils.merge([
        UniformsLib.fog,
        {
          uMap: { value: opts.texture },
          // gl_PointSize est en pixels de device : il faut la hauteur du
          // drawing buffer pour que la taille soit stable d'un ecran a
          // l'autre. three fait exactement ce calcul pour PointsMaterial.
          uScale: { value: 400 },
        },
      ]),
      vertexShader: /* glsl */ `
        #include <common>
        #include <fog_pars_vertex>
        attribute vec3 aColor;
        attribute float aSize;
        attribute float aAlpha;
        uniform float uScale;
        varying vec3 vColor;
        varying float vAlpha;
        void main() {
          vColor = aColor;
          vAlpha = aAlpha;
          vec4 mvPosition = modelViewMatrix * vec4( position, 1.0 );
          gl_PointSize = aSize * ( uScale / - mvPosition.z );
          gl_Position = projectionMatrix * mvPosition;
          #include <fog_vertex>
        }
      `,
      fragmentShader: /* glsl */ `
        #include <common>
        #include <fog_pars_fragment>
        // Pas de #include des "pars" de tone mapping ni d'espace
        // colorimetrique : WebGLProgram les injecte deja dans le prefixe de
        // tout ShaderMaterial, les reinclure casse la compilation.
        uniform sampler2D uMap;
        varying vec3 vColor;
        varying float vAlpha;
        void main() {
          vec4 tex = texture2D( uMap, gl_PointCoord );
          float a = tex.a * vAlpha;
          if ( a < 0.004 ) discard;
          gl_FragColor = vec4( vColor, a );
          #ifdef ADDITIVE
            // Une particule emissive ne se teinte pas de la couleur du
            // brouillard : elle s'eteint. On attenue donc l'alpha au lieu de
            // melanger la couleur, sinon les etincelles lointaines
            // *s'eclaircissent* au lieu de disparaitre.
            // Le chunk fog_pars_fragment ne declare pas les memes uniforms
            // selon le type de brouillard : fogDensity pour FogExp2,
            // fogNear/fogFar pour le lineaire. Se tromper de branche ne donne
            // pas un mauvais rendu, mais un shader qui ne compile pas -- et
            // three.js se contente d'un avertissement par frame.
            #ifdef USE_FOG
              #ifdef FOG_EXP2
                float fogFactor = 1.0 - exp( - fogDensity * fogDensity * vFogDepth * vFogDepth );
              #else
                float fogFactor = smoothstep( fogNear, fogFar, vFogDepth );
              #endif
              gl_FragColor.a *= 1.0 - clamp( fogFactor, 0.0, 1.0 );
            #endif
          #else
            #include <fog_fragment>
          #endif
          #include <tonemapping_fragment>
          #include <colorspace_fragment>
        }
      `,
      defines: opts.blend === 'additive' ? { ADDITIVE: '' } : {},
      transparent: true,
      // Sans ca, une particule proche masquerait celles derriere elle.
      depthWrite: false,
      blending: opts.blend === 'additive' ? AdditiveBlending : NormalBlending,
      fog: true,
    });

    this.points = new Points(this.geometry, this.material);
    // Les particules bougent sans que la boite englobante soit recalculee :
    // laisser le culling actif les ferait disparaitre par paquets.
    this.points.frustumCulled = false;
    this.points.renderOrder = 4;
    scene.add(this.points);

    this.refreshScale();
    window.addEventListener('resize', this.onResize);
  }

  /** Met une particule au monde. Renvoie son indice, pour information. */
  spawn(
    position: Vector3,
    velocity: Vector3,
    life: number,
    color: Color,
    size: number,
  ): number {
    const k = this.cursor;
    this.cursor = (this.cursor + 1) % this.capacity;

    this.positions[k * 3] = position.x;
    this.positions[k * 3 + 1] = position.y;
    this.positions[k * 3 + 2] = position.z;
    this.velocities[k * 3] = velocity.x;
    this.velocities[k * 3 + 1] = velocity.y;
    this.velocities[k * 3 + 2] = velocity.z;
    this.baseColor[k * 3] = color.r;
    this.baseColor[k * 3 + 1] = color.g;
    this.baseColor[k * 3 + 2] = color.b;
    this.baseSize[k] = size;
    this.age[k] = 0;
    this.life[k] = life;
    return k;
  }

  update(dt: number): void {
    const decay = this.drag > 0 ? Math.exp(-this.drag * dt) : 1;

    for (let k = 0; k < this.capacity; k++) {
      const a = this.age[k]!;
      const l = this.life[k]!;
      if (a >= l) {
        // Morte : alpha a zero suffit, le fragment shader la jette.
        this.alphas[k] = 0;
        continue;
      }
      const na = a + dt;
      this.age[k] = na;

      let vx = this.velocities[k * 3]!;
      let vy = this.velocities[k * 3 + 1]! + this.gravity * dt;
      let vz = this.velocities[k * 3 + 2]!;
      vx *= decay;
      vy *= decay;
      vz *= decay;
      this.velocities[k * 3] = vx;
      this.velocities[k * 3 + 1] = vy;
      this.velocities[k * 3 + 2] = vz;

      // Lecture explicite : `+=` sur un acces indexe est refuse sous
      // noUncheckedIndexedAccess, et un Float32Array ne rend jamais undefined.
      this.positions[k * 3] = this.positions[k * 3]! + vx * dt;
      this.positions[k * 3 + 1] = this.positions[k * 3 + 1]! + vy * dt;
      this.positions[k * 3 + 2] = this.positions[k * 3 + 2]! + vz * dt;

      const t = na / l;
      const fade = Math.pow(1 - t, this.fadePower);
      this.alphas[k] = fade;
      this.colors[k * 3] = this.baseColor[k * 3]!;
      this.colors[k * 3 + 1] = this.baseColor[k * 3 + 1]!;
      this.colors[k * 3 + 2] = this.baseColor[k * 3 + 2]!;
      // La fumee et la poussiere grossissent en se diluant.
      this.sizes[k] = this.baseSize[k]! * (1 + t * 1.6);
    }

    (this.geometry.attributes.position as BufferAttribute).needsUpdate = true;
    (this.geometry.attributes.aColor as BufferAttribute).needsUpdate = true;
    (this.geometry.attributes.aSize as BufferAttribute).needsUpdate = true;
    (this.geometry.attributes.aAlpha as BufferAttribute).needsUpdate = true;
  }

  /** Eteint toutes les particules, pour repartir d'une partie propre. */
  reset(): void {
    this.age.fill(1);
    this.alphas.fill(0);
    (this.geometry.attributes.aAlpha as BufferAttribute).needsUpdate = true;
  }

  /**
   * Le canvas occupe toute la fenetre (voir index.html) et le pixel ratio est
   * borne a 2 dans Renderer : la hauteur du drawing buffer s'en deduit sans
   * avoir a tenir une reference au renderer.
   */
  private refreshScale(): void {
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.material.uniforms.uScale!.value = (window.innerHeight * dpr) / 2;
  }

  dispose(): void {
    window.removeEventListener('resize', this.onResize);
    this.geometry.dispose();
    this.material.dispose();
  }
}
